import { forwardRef, RefObject, useRef, useState } from 'react';

import {
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import CurrencyInput from 'react-currency-input-field';

import {
  CheckOutline,
  CreateInvidualRequest,
  Individual,
  DropdownInput,
  useToast,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import useCreateEmployee from 'app/hooks/useCreateEmployee';
import useFetchPreviousEmployment from 'app/hooks/useFetchPreviousEmployment';

import RehireConfirmation from 'app/components/Employees/CreateEmployee/RehireConfirmation';

import { useCreateEmployeeStore } from 'app/store';

import { employeeSalaryFormSchema } from 'app/utils/schemas';

import { parseCurrencyIntoFloat } from 'app/utils/strings';

import {
  EmployeeSalaryFormFields,
  EmployeeFormProps,
  EmployeeDetailsFormFields,
  EmployeePayCalendarFormFields,
  EmployeeCompanyFormFields,
} from '../types';

const EmployeeSalaryForm = forwardRef<HTMLElement, EmployeeFormProps>(
  ({ id, onStepAction, canPrevStep, previousStep }, ref) => {
    const toast = useToast();
    const queryClient = useQueryClient();

    const employeeData = useCreateEmployeeStore((state) => state.employeeData);
    const resetEmployeeData = useCreateEmployeeStore((state) => state.resetEmployeeData);
    const setEmployeeData = useCreateEmployeeStore((state) => state.setEmployeeData);

    const {
      register,
      handleSubmit,
      formState: { errors, isValid },
      getValues,
      setValue,
      watch,
    } = useForm<EmployeeSalaryFormFields>({
      mode: 'onBlur',
      defaultValues: employeeData as EmployeeSalaryFormFields,
      resolver: yupResolver(employeeSalaryFormSchema),
    });

    const watchSalaried = watch('salaried');

    const { companyId: lookupCompanyId } = employeeData as EmployeeCompanyFormFields;
    const { email: lookupEmail } = employeeData as EmployeeDetailsFormFields;
    const { externalPayrollId: lookupPayrollId, payCalendarId: lookupPayCalendarId } =
      employeeData as EmployeePayCalendarFormFields;

    // A failed lookup must never block a create, so the error is deliberately unhandled here: the query simply
    // yields no previous employment and the employee is created without the confirmation step. The in-flight
    // case is handled though — submitting mid-lookup would skip the confirmation entirely.
    //
    // isInitialLoading, not isLoading: react-query v4 reports isLoading on a *disabled* query too, which would
    // leave Create permanently disabled whenever there is nothing to look up.
    const { data: previousEmployment, isInitialLoading: isLoadingPreviousEmployment } =
      useFetchPreviousEmployment({
        companyId: lookupCompanyId,
        payCalendarId: lookupPayCalendarId,
        email: lookupEmail,
        externalPayrollId: lookupPayrollId,
      });

    const [pendingEmployee, setPendingEmployee] = useState<CreateInvidualRequest | null>(null);
    // Which match the admin has already rejected, so pressing Create again goes straight through instead of
    // reopening the prompt they just dismissed. Held as the record's id rather than a flag: going back and
    // entering a different person must ask again, and comparing ids means that happens on its own.
    const [dismissedPreviousId, setDismissedPreviousId] = useState<string | null>(null);
    const cancelRehireRef = useRef<HTMLButtonElement>(null);

    const { mutate: createEmployee, isLoading: isCreatingEmployee } = useCreateEmployee({
      onSuccess: (employee: Individual) => {
        toast({
          position: 'top-right',
          status: 'success',
          title: 'New employee created',
          description: `${employee.firstName} ${employee.lastName} was successfully created!`,
        });

        queryClient.invalidateQueries({ queryKey: ['fetchEmployeesPage'] });
        queryClient.invalidateQueries({ queryKey: ['fetchEmployeesStats'] });

        resetEmployeeData();

        onStepAction();
      },
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'Sorry, the new employee was not created. Please try again.',
        });
      },
    });

    const handleCreateEmployee = (data: EmployeeSalaryFormFields) => {
      const { companyId } = employeeData as EmployeeCompanyFormFields;
      const { firstName, lastName, email, additionalEmail } =
        employeeData as EmployeeDetailsFormFields;
      const { payCalendarId, startDate, externalPayrollId } =
        employeeData as EmployeePayCalendarFormFields;
      const { annualGrossSalary, salaried } = data;

      const annualGrossSalaryParsed = parseCurrencyIntoFloat(annualGrossSalary?.toString());

      const request = {
        firstName,
        lastName,
        email,
        additionalEmail,
        payCalendarId,
        startDate,
        externalPayrollId,
        annualGrossSalary:
          salaried && !Number.isNaN(annualGrossSalaryParsed) ? annualGrossSalaryParsed : undefined,
        salaried,
        role: 'EMPLOYEE' as const,
        invite: false,
        companyId,
      };

      if (previousEmployment && previousEmployment.id !== dismissedPreviousId) {
        setPendingEmployee(request);
        return;
      }

      createEmployee(request);
    };

    const handleConfirmRehire = () => {
      if (!pendingEmployee) {
        return;
      }

      createEmployee({ ...pendingEmployee, previousIndividualId: previousEmployment?.id });
      setPendingEmployee(null);
    };

    // Cancelling drops the confirmation and leaves the entered values untouched, so the admin returns to the form
    // exactly as they left it — and pressing Create will ask again.
    const handleCancelRehire = () => setPendingEmployee(null);

    // "Not the same person" is the opposite: the admin has judged the match wrong, so the employee is created
    // unlinked. Payroll ids are only unique within a pay calendar and emails get reused, so a match is a prompt
    // and never a verdict — without this the admin would have to alter real data to get past it.
    const handleRejectRehire = () => {
      if (!pendingEmployee) {
        return;
      }

      setDismissedPreviousId(previousEmployment?.id ?? null);
      createEmployee(pendingEmployee);
      setPendingEmployee(null);
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      setEmployeeData(formValues);
      previousStep();
    };

    const salariedOptions = [
      { value: 'true', text: 'Yes' },
      { value: 'false', text: 'No' },
    ];

    return (
      <>
        <RehireConfirmation
          previousEmployment={previousEmployment ?? null}
          isOpen={!!pendingEmployee}
          isCreating={isCreatingEmployee}
          cancelRef={cancelRehireRef}
          onConfirm={handleConfirmRehire}
          onReject={handleRejectRehire}
          onCancel={handleCancelRehire}
        />
        <form id={id} onSubmit={handleSubmit(handleCreateEmployee)} noValidate>
          <Stack spacing="20px">
            <FormControl isInvalid={!!errors?.salaried?.message} isRequired>
              <FormLabel>Salaried employee</FormLabel>
              <DropdownInput
                placeholder="Select salaried status"
                defaultValue={
                  salariedOptions.find((option) => option.value === watchSalaried.toString()) ||
                  salariedOptions[1]
                }
                options={salariedOptions}
                onChange={(value) => {
                  const isSalaried = value === 'true';
                  setValue('salaried', isSalaried);
                  if (!isSalaried) setValue('annualGrossSalary', '');
                }}
                onBlur={() => {}}
              />
              <FormErrorMessage>{errors?.salaried?.message}</FormErrorMessage>
            </FormControl>

            {watchSalaried && (
              <FormControl isInvalid={!!errors?.annualGrossSalary?.message}>
                <FormLabel>Annual gross salary</FormLabel>
                <InputGroup>
                  <Input
                    as={CurrencyInput}
                    prefix={'\u00A3'}
                    decimalSeparator="."
                    groupSeparator=","
                    decimalScale={2}
                    defaultValue={
                      (employeeData as EmployeeSalaryFormFields)?.annualGrossSalary
                        ? parseCurrencyIntoFloat(
                            (employeeData as EmployeeSalaryFormFields).annualGrossSalary
                          )
                        : undefined
                    }
                    type="text"
                    placeholder={'Enter annual gross salary. E.g. \u00A324,000.00'}
                    pr="86px"
                    {...register('annualGrossSalary')}
                  />
                  <InputRightElement w="86px">
                    <HStack spacing="18px" height="32px">
                      <Divider borderColor="brand.mediumGray" orientation="vertical" />
                      <Text as="h6" color="brand.darkGray" textStyle="h6Medium">
                        GBP
                      </Text>
                    </HStack>
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors?.annualGrossSalary?.message}</FormErrorMessage>
              </FormControl>
            )}

            <Portal containerRef={ref as RefObject<HTMLElement>}>
              <Button
                size="sm"
                variant="outline"
                isDisabled={!canPrevStep}
                leftIcon={<ArrowLeftOutline boxSize="20px" />}
                onClick={handleBackCLick}
                mr="16px"
              >
                Back
              </Button>
              <Button
                form={id}
                type="submit"
                size="sm"
                isDisabled={!isValid || isLoadingPreviousEmployment}
                isLoading={isCreatingEmployee || isLoadingPreviousEmployment}
                rightIcon={<CheckOutline boxSize="20px" />}
              >
                Create
              </Button>
            </Portal>
          </Stack>
        </form>
      </>
    );
  }
);

EmployeeSalaryForm.displayName = 'EmployeeSalaryForm';

export default EmployeeSalaryForm;
